<script setup lang="tsx">
import { useAbsoluteUrl } from '~/composables/useAbsoluteUrl';
import { useWishlistLoadingState } from '~/composables/useWishlists/useWishlistLoadingState';
import { grammarInflection } from '~~/utils';

import { Container } from '~/components/Container';
import LoadingScreen from '~/components/LoadingScreen';

definePageMeta({
	name: 'MyWishlist',
	layout: 'properties-listing-contextual'
});

// Estado compartilhado da wishlist com loading state consistente
const { isWishlistLoading, isWishlistDataReady, wishlistsState } =
	useWishlistLoadingState();

// Tracks if the initial data load has completed
// Used to show loading only on first page load, not on subsequent refreshes (like unfavoriting)
const hasInitiallyLoaded = ref(false);

// Mark as initially loaded once data is ready for the first time
watch(
	isWishlistDataReady,
	isReady => {
		if (isReady && !hasInitiallyLoaded.value) {
			hasInitiallyLoaded.value = true;
		}
	},
	{ immediate: true }
);

// Show loading only on initial page load, not on refreshes (unfavorite, etc.)
const shouldShowInitialLoading = computed(
	() => isWishlistLoading.value && !hasInitiallyLoaded.value
);

// Authwall para proteger esta página - abre modal se não autenticado
const { isAuthenticated } = useAuthwall({ autoStart: true });

const userWishlists = computed(() => wishlistsState.value?.wishlists ?? []);

const mainWishlist = computed(() => {
	return userWishlists.value?.find(wishlist => wishlist.mainWishlist);
});

const numberOfPropertiesOnMainWishlist = computed(() => {
	return mainWishlist.value?.properties.length;
});

const pageDescription = computed(() => {
	const propertyCount = numberOfPropertiesOnMainWishlist.value ?? 0;
	const defaultDescription =
		'Seus imóveis favoritos sempre com você. Salve, organize e acompanhe todos em um único lugar.';

	if (propertyCount === 0) {
		return defaultDescription;
	}

	const propertiesWord = grammarInflection(propertyCount, 'imóvel', 'imóveis');
	return `${propertyCount} ${propertiesWord} na sua Coleção. ${defaultDescription}`;
});

const { toAbsoluteUrl } = useAbsoluteUrl();
const ogImageUrl = toAbsoluteUrl('/images/social/openGraph/og_home.webp');
useSeoMeta({
	title: 'Minha Coleção de Imóveis | Pilar Homes',
	description: () => pageDescription.value,
	ogSiteName: 'Pilar Homes',
	ogTitle: 'Minha Coleção de Imóveis | Pilar Homes',
	ogDescription: () => pageDescription.value,
	ogImage: ogImageUrl,
	twitterTitle: 'Minha Coleção de Imóveis | Pilar Homes',
	twitterDescription: () => pageDescription.value,
	twitterCard: 'summary_large_image',
	twitterImage: ogImageUrl
});

const mainWishlistId = computed<string | undefined>(() => {
	const id = mainWishlist.value?.id;
	return id;
});

const hasWishlists = computed<boolean>(() => {
	const count = mainWishlist.value?.properties.length ?? 0;
	const result = count > 0;
	return result;
});

// Cache da função carregada dinamicamente
let setCurrentWishlistIdFn: ((id: string) => void) | null = null;
let isComponentMounted = true;

// Carrega a função no mount
onMounted(async () => {
	const { useWishlists } = await import('~/composables/useWishlists/wishlists');
	if (!isComponentMounted) return;

	const { setCurrentWishlistId } = await useWishlists();
	if (!isComponentMounted) return;

	setCurrentWishlistIdFn = setCurrentWishlistId;

	// Se mainWishlistId já está disponível, chama imediatamente
	if (mainWishlistId.value) {
		setCurrentWishlistId(mainWishlistId.value);
	}
});

onUnmounted(() => {
	isComponentMounted = false;
	setCurrentWishlistIdFn = null;
});

// Watch registrado no setup (cleanup automático garantido)
watch(mainWishlistId, newId => {
	if (newId && setCurrentWishlistIdFn) {
		setCurrentWishlistIdFn(newId);
	}
});

const shouldShowWishlistView = computed(() => {
	const hasWishlistsValue = hasWishlists.value;
	const numberOfProperties = numberOfPropertiesOnMainWishlist?.value;
	const result =
		hasWishlistsValue && numberOfProperties && numberOfProperties > 0;

	return result;
});
</script>

<template>
	<div class="flex w-full items-center justify-center">
		<!-- SSR: Mostra estrutura base com loading -->
		<ClientOnly>
			<template #fallback>
				<div class="flex min-h-dvh w-full items-center justify-center">
					<LoadingScreen
						container-classes="min-h-dvh w-full"
						icon-classes="h-12 w-12 text-primary"
					/>
				</div>
			</template>

			<!-- Client: Mostra conteúdo baseado no estado real -->
			<template v-if="isAuthenticated">
				<!-- Loading only on initial page load, not on refreshes (unfavorite, etc.) -->
				<div
					v-if="shouldShowInitialLoading"
					class="flex min-h-dvh w-full items-center justify-center"
				>
					<LoadingScreen
						container-classes="min-h-dvh w-full"
						icon-classes="h-12 w-12 text-primary"
					/>
				</div>

				<!-- Wishlist com propriedades -->
				<Suspense v-else-if="shouldShowWishlistView">
					<WishlistView
						:wishlist-id="mainWishlistId"
						:wishlist-data="mainWishlist"
						:is-own-wishlist="true"
					/>
					<template #fallback>
						<LoadingScreen
							container-classes="min-h-dvh w-full"
							icon-classes="h-12 w-12 text-primary"
						/>
					</template>
				</Suspense>

				<!-- Onboarding (wishlist vazia) -->
				<div
					v-else
					class="h-[calc(100svh-112px)] w-full md:min-h-[calc(100dvh-120px)]"
				>
					<OnboardingCollections data-testid="onboarding-collections" />
					<Container class="bg-primary-light-4 py-0">
						<NavigationSectionNavigation
							links-container-wrapper-variant="column"
						/>
					</Container>
					<Container class="bg-primary !py-0">
						<PortalFooter />
					</Container>
				</div>
			</template>

			<!-- Não autenticado (modal de login está aberta) -->
			<div v-else class="flex min-h-dvh w-full items-center justify-center">
				<LoadingScreen
					container-classes="min-h-dvh w-full"
					icon-classes="h-12 w-12 text-primary"
				/>
			</div>
		</ClientOnly>
	</div>
</template>
